import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { toast } from 'sonner';
import { printCard } from '../lib/receipt';
import { cardLabels, CARD_LANGUAGES } from '../lib/cardLabels';
import { QRCodeCanvas } from 'qrcode.react';
import { PageHeader } from '../components/common/PageHeader';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Gift, Plus, Search, Settings2, Trash2, Star, Award, UserPlus, X, Minus, QrCode, Printer, Lock, ScrollText,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSecurityPin } from '../context/SecurityPinContext';
import { AuditLogDialog } from '../components/common/AuditLogDialog';

const fmtEur = (n) => `€${(Number(n) || 0).toFixed(2)}`;
const publicCardUrl = (code) => `${window.location.origin}/carta/fedelta/${code}`;

export default function LoyaltyPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canManage = user?.role === 'admin' || user?.role === 'manager';
  const { runSensitive } = useSecurityPin();
  const [auditOpen, setAuditOpen] = useState(false);
  const [config, setConfig] = useState(null);
  const [members, setMembers] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '', language: 'es' });
  const [saving, setSaving] = useState(false);

  const [selected, setSelected] = useState(null);
  const [printLang, setPrintLang] = useState('es');
  const [earnAmount, setEarnAmount] = useState('');
  const [adjust, setAdjust] = useState('');

  const [cfgOpen, setCfgOpen] = useState(false);
  const [cfgForm, setCfgForm] = useState({ enabled: true, points_per_euro: 1, rewards: [] });
  const qrRef = useRef(null);
  const [printing, setPrinting] = useState(false);

  const loadMembers = useCallback(async (search) => {
    const { data } = await api.get('/loyalty/members', { params: search ? { q: search } : {} });
    setMembers(data || []);
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: cfg }] = await Promise.all([api.get('/loyalty/config')]);
      setConfig(cfg);
      setCfgForm({ enabled: cfg.enabled !== false, points_per_euro: cfg.points_per_euro ?? 1, rewards: cfg.rewards || [] });
      await loadMembers('');
    } catch (e) {
      toast.error(t('loyalty.loadError'));
    } finally {
      setLoading(false);
    }
  }, [loadMembers, t]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Ricerca con piccolo debounce.
  useEffect(() => {
    const id = setTimeout(() => { loadMembers(q).catch(() => {}); }, 300);
    return () => clearTimeout(id);
  }, [q, loadMembers]);

  const createMember = async () => {
    if (!form.name.trim()) { toast.error(t('loyalty.errName')); return; }
    setSaving(true);
    try {
      const { data } = await runSensitive((cfg) => api.post('/loyalty/members', form, cfg));
      toast.success(t('loyalty.cardCreated'));
      setNewOpen(false);
      setForm({ name: '', phone: '', email: '', language: 'es' });
      await loadMembers(q);
      openMember(data.id);
    } catch (e) {
      if (e?.message === 'cancelled') { setSaving(false); return; }
      toast.error(e?.response?.data?.detail || t('loyalty.createError'));
    } finally {
      setSaving(false);
    }
  };

  const openMember = async (id) => {
    try {
      const { data } = await api.get(`/loyalty/members/${id}`);
      setSelected(data);
      setPrintLang(data.language || 'es');
      setEarnAmount('');
      setAdjust('');
    } catch (e) {
      toast.error(t('loyalty.memberNotFound'));
    }
  };

  const refreshSelected = (data) => {
    setSelected(data);
    setMembers((prev) => prev.map((m) => (m.id === data.id ? { ...m, points: data.points, total_earned: data.total_earned } : m)));
  };

  const doEarn = async () => {
    const amt = parseFloat(earnAmount);
    if (!amt || amt <= 0) { toast.error(t('loyalty.errAmount')); return; }
    try {
      const { data } = await api.post(`/loyalty/members/${selected.id}/earn`, { amount: amt });
      refreshSelected(data);
      setEarnAmount('');
      toast.success(t('loyalty.pointsEarned'));
    } catch (e) { toast.error(e?.response?.data?.detail || t('common.error')); }
  };

  const doAdjust = async (sign) => {
    const v = parseInt(adjust, 10);
    if (!v || v <= 0) { toast.error(t('loyalty.errPoints')); return; }
    try {
      const { data } = await runSensitive((cfg) => api.post(`/loyalty/members/${selected.id}/adjust`, { points: sign * v, note: sign > 0 ? t('loyalty.manualAdd') : t('loyalty.manualSub') }, cfg));
      refreshSelected(data);
      setAdjust('');
      toast.success(t('loyalty.pointsUpdated'));
    } catch (e) { if (e?.message !== 'cancelled') toast.error(e?.response?.data?.detail || t('common.error')); }
  };

  const doRedeem = async (reward) => {
    if ((selected.points || 0) < reward.points_cost) { toast.error(t('loyalty.notEnough')); return; }
    try {
      const { data } = await api.post(`/loyalty/members/${selected.id}/redeem`, { reward_id: reward.id });
      refreshSelected(data);
      toast.success(t('loyalty.rewardRedeemed', { name: reward.name }));
    } catch (e) { toast.error(e?.response?.data?.detail || t('common.error')); }
  };

  const deleteMember = async () => {
    if (!window.confirm(t('loyalty.deleteConfirm'))) return;
    try {
      await runSensitive((cfg) => api.delete(`/loyalty/members/${selected.id}`, cfg));
      toast.success(t('loyalty.cardDeleted'));
      setSelected(null);
      await loadMembers(q);
    } catch (e) { if (e?.message !== 'cancelled') toast.error(e?.response?.data?.detail || t('common.error')); }
  };

  // Stampa la tessera fedeltà su scontrino verticale (valori reali aggiornati).
  const printLoyalty = async () => {
    if (!selected) return;
    setPrinting(true);
    try {
      // Recupera valori reali + branding dalla stessa fonte della pagina pubblica.
      const { data } = await api.get(`/public/loyalty/${selected.code}`);
      const lab = cardLabels(printLang);
      const url = publicCardUrl(selected.code);
      let qrDataUrl = null;
      try { qrDataUrl = qrRef.current ? qrRef.current.toDataURL('image/png') : null; } catch { /* */ }
      await printCard({
        businessName: data?.business?.name || '',
        logo: data?.business?.logo || null,
        title: lab.loyaltyTitle,
        lines: [data?.member?.name || selected.name],
        valueLabel: lab.points,
        valueText: String(data?.member?.points ?? selected.points),
        code: selected.code,
        qrUrl: url,
        qrHint: lab.loyaltyHint,
        footer: data?.business?.name || '',
      }, { qrDataUrl });
      toast.success(t('loyalty.cardSentToPrinter'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('loyalty.printError'));
    } finally {
      setPrinting(false);
    }
  };

  // ----- Config -----
  const addRewardRow = () => setCfgForm((c) => ({ ...c, rewards: [...c.rewards, { id: undefined, name: '', points_cost: 100, description: '' }] }));
  const updateRewardRow = (i, key, val) => setCfgForm((c) => ({ ...c, rewards: c.rewards.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)) }));
  const removeRewardRow = (i) => setCfgForm((c) => ({ ...c, rewards: c.rewards.filter((_, idx) => idx !== i) }));

  const saveConfig = async () => {
    try {
      const payload = {
        enabled: cfgForm.enabled,
        points_per_euro: parseFloat(cfgForm.points_per_euro) || 0,
        rewards: cfgForm.rewards
          .filter((r) => r.name && r.name.trim())
          .map((r) => ({ id: r.id, name: r.name.trim(), points_cost: parseInt(r.points_cost, 10) || 0, description: r.description || '' })),
      };
      const { data } = await runSensitive((cfg) => api.put('/loyalty/config', payload, cfg));
      setConfig(data);
      setCfgForm({ enabled: data.enabled !== false, points_per_euro: data.points_per_euro, rewards: data.rewards || [] });
      setCfgOpen(false);
      toast.success(t('loyalty.configSaved'));
    } catch (e) { if (e?.message !== 'cancelled') toast.error(e?.response?.data?.detail || t('loyalty.saveError')); }
  };

  return (
    <div className="p-4 md:p-6 max-w-5xl" data-testid="loyalty-page">
      <PageHeader title={t('loyalty.title')} subtitle={t('loyalty.subtitle')}>
        {canManage ? (
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-xl" onClick={() => setAuditOpen(true)} data-testid="loyalty-audit-button">
              <ScrollText className="h-4 w-4 mr-2" /> {t('loyalty.register')}
            </Button>
            <Button variant="outline" className="rounded-xl" onClick={() => setCfgOpen(true)} data-testid="loyalty-config-button">
              <Settings2 className="h-4 w-4 mr-2" /> {t('loyalty.configuration')}
            </Button>
            <Button className="rounded-xl" onClick={() => setNewOpen(true)} data-testid="loyalty-new-button">
              <UserPlus className="h-4 w-4 mr-2" /> {t('loyalty.newCard')}
            </Button>
          </div>
        ) : (
          <Badge variant="outline" className="text-xs bg-muted text-muted-foreground" data-testid="loyalty-readonly-badge">
            <Lock className="h-3 w-3 mr-1" /> {t('loyalty.readOnly')}
          </Badge>
        )}
      </PageHeader>

      {/* Riepilogo regola punti */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Badge variant="secondary" className="rounded-lg text-sm py-1.5 px-3" data-testid="loyalty-rate-badge">
          <Star className="h-4 w-4 mr-1.5 text-primary" /> {t('loyalty.rateBadge', { n: config?.points_per_euro ?? 1 })}
        </Badge>
        <Badge variant="secondary" className="rounded-lg text-sm py-1.5 px-3">
          <Award className="h-4 w-4 mr-1.5 text-primary" /> {t('loyalty.rewardsBadge', { count: config?.rewards?.length || 0 })}
        </Badge>
        <Badge variant="secondary" className="rounded-lg text-sm py-1.5 px-3">
          <Gift className="h-4 w-4 mr-1.5 text-primary" /> {t('loyalty.cardsBadge', { count: members.length })}
        </Badge>
      </div>

      {/* Ricerca */}
      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          className="pl-10"
          placeholder={t('loyalty.searchPlaceholder')}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="loyalty-search-input"
        />
      </div>

      {/* Lista soci */}
      {loading ? (
        <div className="text-muted-foreground p-6">{t('common.loading')}</div>
      ) : members.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground" data-testid="loyalty-empty">
          <Gift className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <div className="font-medium">{t('loyalty.emptyTitle')}</div>
          <div className="text-sm">{t('loyalty.emptyDesc')}</div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {members.map((m) => (
            <button
              key={m.id}
              onClick={() => openMember(m.id)}
              className="text-left rounded-2xl border border-border bg-card p-4 transition-colors hover:border-primary hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              data-testid={`loyalty-member-${m.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold truncate">{m.name}</div>
                  <div className="text-xs text-muted-foreground font-mono mt-0.5">{m.code}</div>
                  {m.phone ? <div className="text-xs text-muted-foreground mt-0.5">{m.phone}</div> : null}
                </div>
                <div className="text-right shrink-0">
                  <div className="text-2xl font-bold text-primary leading-none">{m.points}</div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground mt-1">{t('loyalty.points')}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Dialog: nuova tessera */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="sm:max-w-md" data-testid="loyalty-new-dialog">
          <DialogHeader><DialogTitle>{t('loyalty.newCardTitle')}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t('loyalty.customerName')}</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Mario Rossi" data-testid="loyalty-form-name" />
            </div>
            <div>
              <Label>{t('loyalty.phone')}</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+39…" data-testid="loyalty-form-phone" />
            </div>
            <div>
              <Label>{t('loyalty.email')}</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@cliente.it" data-testid="loyalty-form-email" />
            </div>
            <div>
              <Label>{t('loyalty.receiptLanguage')}</Label>
              <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
                <SelectTrigger className="mt-1" data-testid="loyalty-form-language"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CARD_LANGUAGES.map((l) => <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">{t('loyalty.receiptLanguageHint')}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setNewOpen(false)}>{t('common.cancel')}</Button>
            <Button className="rounded-xl" onClick={createMember} disabled={saving} data-testid="loyalty-form-save">{t('loyalty.createCard')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: dettaglio socio */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="loyalty-member-dialog">
          {selected && (
            <>
              <DialogHeader><DialogTitle>{selected.name}</DialogTitle></DialogHeader>
              <ScrollArea className="max-h-[70vh] pr-3">
                <div className="space-y-4">
                  {/* QR + punti */}
                  <div className="flex items-center gap-4 rounded-2xl bg-muted/50 p-4">
                    <div className="bg-white p-2 rounded-xl shrink-0">
                      <QRCodeCanvas ref={qrRef} value={publicCardUrl(selected.code)} size={104} includeMargin data-testid="loyalty-qr" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs text-muted-foreground">{t('loyalty.cardCode')}</div>
                      <div className="font-mono font-semibold tracking-wider">{selected.code}</div>
                      <div className="mt-2 text-3xl font-bold text-primary leading-none" data-testid="loyalty-points">{selected.points} <span className="text-base font-medium text-muted-foreground">{t('loyalty.points')}</span></div>
                      {selected.phone ? <div className="text-xs text-muted-foreground mt-1">{selected.phone}</div> : null}
                    </div>
                  </div>

                  {/* Stampa tessera */}
                  <div className="flex gap-2">
                    <Select value={printLang} onValueChange={setPrintLang}>
                      <SelectTrigger className="w-36 shrink-0" data-testid="loyalty-print-language"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CARD_LANGUAGES.map((l) => <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button variant="outline" className="flex-1 rounded-xl" onClick={printLoyalty} disabled={printing} data-testid="loyalty-print-button">
                      <Printer className="h-4 w-4 mr-2" /> {printing ? t('loyalty.printing') : t('loyalty.printCard')}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground -mt-2">{t('loyalty.printHint')}</p>

                  {/* Accumula da spesa */}
                  <div>
                    <Label>{t('loyalty.earnFromSpend')}</Label>
                    <div className="flex gap-2 mt-1">
                      <Input type="number" inputMode="decimal" placeholder={t('loyalty.amountPlaceholder')} value={earnAmount} onChange={(e) => setEarnAmount(e.target.value)} data-testid="loyalty-earn-amount" />
                      <Button className="rounded-xl shrink-0" onClick={doEarn} data-testid="loyalty-earn-button"><Plus className="h-4 w-4 mr-1" /> {t('loyalty.earn')}</Button>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">{t('loyalty.ratePerEuro', { n: config?.points_per_euro ?? 1 })}</div>
                  </div>

                  {/* Premi */}
                  <div>
                    <Label className="mb-2 block">{t('loyalty.redeemReward')}</Label>
                    {(!config?.rewards || config.rewards.length === 0) ? (
                      <div className="text-sm text-muted-foreground">{t('loyalty.noRewardsConfigured')}</div>
                    ) : (
                      <div className="space-y-2">
                        {config.rewards.map((r) => {
                          const enough = (selected.points || 0) >= r.points_cost;
                          return (
                            <div key={r.id} className="flex items-center justify-between gap-2 rounded-xl border border-border p-3">
                              <div className="min-w-0">
                                <div className="font-medium truncate">{r.name}</div>
                                {r.description ? <div className="text-xs text-muted-foreground truncate">{r.description}</div> : null}
                                <div className="text-xs text-primary font-medium mt-0.5">{r.points_cost} {t('loyalty.points')}</div>
                              </div>
                              <Button size="sm" variant={enough ? 'default' : 'outline'} className="rounded-lg shrink-0" disabled={!enough} onClick={() => doRedeem(r)} data-testid={`loyalty-redeem-${r.id}`}>
                                <Award className="h-4 w-4 mr-1" /> {t('loyalty.redeem')}
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Rettifica manuale */}
                  {canManage && (
                  <div>
                    <Label>{t('loyalty.manualAdjust')}</Label>
                    <div className="flex gap-2 mt-1">
                      <Input type="number" inputMode="numeric" placeholder={t('loyalty.pointsPlaceholder')} value={adjust} onChange={(e) => setAdjust(e.target.value)} data-testid="loyalty-adjust-input" />
                      <Button variant="outline" className="rounded-xl shrink-0" onClick={() => doAdjust(1)} data-testid="loyalty-adjust-add"><Plus className="h-4 w-4" /></Button>
                      <Button variant="outline" className="rounded-xl shrink-0" onClick={() => doAdjust(-1)} data-testid="loyalty-adjust-sub"><Minus className="h-4 w-4" /></Button>
                    </div>
                  </div>
                  )}

                  {/* Storico */}
                  {selected.history && selected.history.length > 0 && (
                    <div>
                      <Label className="mb-2 block">{t('loyalty.history')}</Label>
                      <div className="space-y-1.5">
                        {selected.history.slice(0, 12).map((h, i) => (
                          <div key={i} className="flex items-center justify-between text-sm border-b border-border/60 pb-1.5">
                            <span className="text-muted-foreground truncate mr-2">{h.note || h.type}</span>
                            <span className={`font-semibold shrink-0 ${h.points >= 0 ? 'text-pos-free' : 'text-destructive'}`}>{h.points >= 0 ? '+' : ''}{h.points}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </ScrollArea>
              <DialogFooter className="flex-row justify-between sm:justify-between">
                {canManage ? (
                  <Button variant="ghost" className="rounded-xl text-destructive hover:text-destructive" onClick={deleteMember} data-testid="loyalty-delete-button">
                    <Trash2 className="h-4 w-4 mr-1" /> {t('common.delete')}
                  </Button>
                ) : <span />}
                <Button variant="outline" className="rounded-xl" onClick={() => setSelected(null)}>{t('common.close')}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog: configurazione */}
      <Dialog open={cfgOpen} onOpenChange={setCfgOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="loyalty-config-dialog">
          <DialogHeader><DialogTitle>{t('loyalty.configTitle')}</DialogTitle></DialogHeader>
          <ScrollArea className="max-h-[70vh] pr-3">
            <div className="space-y-4">
              <div className="flex items-center justify-between rounded-xl border border-border p-3">
                <div>
                  <div className="font-medium">{t('loyalty.programActive')}</div>
                  <div className="text-xs text-muted-foreground">{t('loyalty.programActiveDesc')}</div>
                </div>
                <Switch checked={cfgForm.enabled} onCheckedChange={(v) => setCfgForm({ ...cfgForm, enabled: v })} data-testid="loyalty-cfg-enabled" />
              </div>
              <div>
                <Label>{t('loyalty.pointsPerEuro')}</Label>
                <Input type="number" inputMode="decimal" value={cfgForm.points_per_euro} onChange={(e) => setCfgForm({ ...cfgForm, points_per_euro: e.target.value })} data-testid="loyalty-cfg-rate" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>{t('loyalty.rewardsThresholds')}</Label>
                  <Button size="sm" variant="outline" className="rounded-lg" onClick={addRewardRow} data-testid="loyalty-cfg-add-reward"><Plus className="h-4 w-4 mr-1" /> {t('loyalty.addReward')}</Button>
                </div>
                <div className="space-y-2">
                  {cfgForm.rewards.length === 0 && <div className="text-sm text-muted-foreground">{t('loyalty.noRewardsHint')}</div>}
                  {cfgForm.rewards.map((r, i) => (
                    <div key={i} className="flex items-start gap-2 rounded-xl border border-border p-2">
                      <div className="flex-1 space-y-2">
                        <Input placeholder={t('loyalty.rewardNamePlaceholder')} value={r.name} onChange={(e) => updateRewardRow(i, 'name', e.target.value)} data-testid={`loyalty-cfg-reward-name-${i}`} />
                        <div className="flex gap-2">
                          <Input type="number" inputMode="numeric" placeholder={t('loyalty.pointsPlaceholder')} value={r.points_cost} onChange={(e) => updateRewardRow(i, 'points_cost', e.target.value)} className="w-32" data-testid={`loyalty-cfg-reward-cost-${i}`} />
                          <Input placeholder={t('loyalty.descriptionOptional')} value={r.description || ''} onChange={(e) => updateRewardRow(i, 'description', e.target.value)} />
                        </div>
                      </div>
                      <Button size="icon" variant="ghost" className="text-destructive shrink-0" onClick={() => removeRewardRow(i)} data-testid={`loyalty-cfg-reward-del-${i}`}><X className="h-4 w-4" /></Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setCfgOpen(false)}>{t('common.cancel')}</Button>
            <Button className="rounded-xl" onClick={saveConfig} data-testid="loyalty-cfg-save">{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AuditLogDialog open={auditOpen} onOpenChange={setAuditOpen} title={t('loyalty.auditTitle')} />
    </div>
  );
}
